import React from "react";
import LinkCard, { linkCardStyles as styles } from "./LinkCard";

interface TireBrowseCardProps {
  // The link to browse tires on TireRack.com (a size search, a category page, or a
  // no-results fallback). Required.
  searchUrl: string;
  // find_tires path (TRAC-295): a precomputed human label for whatever filtered the
  // link — a size ("245/40R18"), a category ("All-Terrain tires"), or both. When set,
  // it's shown verbatim and takes precedence over `tireSize`.
  title?: string;
  // Legacy "Browse tires by size" path (TRAC-396): the size, shown prominently.
  tireSize?: string;
  ctaText?: string;
  // Staggered fitments (size path) — when both are set, render Front/Rear stacked.
  frontTireSize?: string;
  rearTireSize?: string;
  isStaggered?: boolean;
}

/**
 * TireBrowseCard — the single card for any "go browse tires" link, however it was
 * filtered. Renders through the shared {@link LinkCard} so its format (eyebrow +
 * prominent label + CTA button) matches every other Tire Rack destination card
 * (navigation_card et al.); this component just computes the tire-specific copy:
 *  - Title (find_tires): the precomputed label ("All-Terrain tires", "225/65R17"),
 *    eyebrow "Tire search".
 *  - Single size (legacy browse-by-size): the size, eyebrow "Search by size".
 *  - Staggered: stacked "Front: …" / "Rear: …" body, eyebrow "Search by size — staggered".
 *
 * (Replaces the old size-only SizeSearchLinkCard and the never-rendered
 * category_browse_card — one component now covers every tire-browse link.)
 */
const TireBrowseCard: React.FC<TireBrowseCardProps> = ({
  searchUrl,
  title,
  tireSize,
  ctaText,
  frontTireSize,
  rearTireSize,
  isStaggered,
}) => {
  const staggered =
    !title && Boolean(isStaggered) && Boolean(frontTireSize) && Boolean(rearTireSize);

  // The prominent label: an explicit title wins, else the size.
  const label = title || tireSize;

  const eyebrow = title
    ? "Tire search"
    : staggered
      ? "Search by size — staggered"
      : "Search by size";
  const cta = ctaText || (staggered ? "Browse staggered tires →" : "Browse tires →");
  const trackOption = staggered ? `${frontTireSize} + ${rearTireSize}` : label || "";

  // Staggered body: stacked Front:/Rear: rows (vs the single prominent label).
  const body = staggered ? (
    <div className={styles.staggeredRows}>
      <div className={styles.row}>
        <span className={styles.rowLabel}>Front:</span>
        <span className={styles.size}>{frontTireSize}</span>
      </div>
      <div className={styles.row}>
        <span className={styles.rowLabel}>Rear:</span>
        <span className={styles.size}>{rearTireSize}</span>
      </div>
    </div>
  ) : undefined;

  return (
    <LinkCard
      url={searchUrl}
      eyebrow={eyebrow}
      label={staggered ? undefined : label}
      body={body}
      ctaText={cta}
      trackId="tire_browse_card"
      trackOption={trackOption}
      className={staggered ? styles.cardStaggered : undefined}
      cardData={{ "data-staggered": staggered ? "true" : "false" }}
    />
  );
};

export default TireBrowseCard;
